import React from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { LegalPage } from '../components/legal-page';

const SECTIONS = [
  {
    heading: '1. Acceptance of Terms',
    body: [
      'These Terms of Use ("Terms") govern your access to and use of Kast, including our website, applications, and any related services (collectively, the "Service"). By creating an account or using the Service, you agree to be bound by these Terms.',
      'If you do not agree to these Terms, please do not use the Service.',
    ],
  },
  {
    heading: '2. Your Account',
    body: [
      'You must provide accurate information when creating a Kast account and keep your login credentials secure. You are responsible for all activity that happens under your account.',
      'Kast accounts can hold multiple viewer profiles, including Kids profiles with restricted content. You are responsible for managing who has access to your account and profiles.',
    ],
  },
  {
    heading: '3. Subscriptions & Billing',
    body: [
      'Some content and features require a paid subscription. Subscription fees are billed in advance on a recurring basis until you cancel.',
      [
        'Prices may change from time to time; we will give you notice before any change takes effect.',
        'You can cancel your subscription at any time from your account settings; access continues until the end of the current billing period.',
        'Fees are non-refundable except where required by law.',
      ],
    ],
  },
  {
    heading: '4. Content & Intellectual Property',
    body: [
      'All movies, series, images, and other material made available through Kast are owned by Kast or its licensors and content creators, and are protected by copyright and other intellectual property laws.',
      'We grant you a limited, non-exclusive, non-transferable licence to stream content for personal, non-commercial use. You may not download, copy, redistribute, publicly perform, or create derivative works from Kast content except where the Service expressly allows it.',
    ],
  },
  {
    heading: '5. Content Creator Uploads',
    body: [
      'Kast allows approved content creators to upload movies and series to the platform. If you participate as a content creator, you confirm that you own or have the necessary rights to all content you upload, and that it does not infringe on the rights of any third party.',
      'Payouts to content creators are calculated and processed according to the terms presented to you in the Creator Studio and may be updated from time to time.',
    ],
  },
  {
    heading: '6. Prohibited Conduct',
    body: [
      'When using Kast, you agree not to:',
      [
        'Share your account or password with people outside your household in a way that violates your subscription plan.',
        'Attempt to circumvent content protection, access controls, or regional availability restrictions.',
        'Upload content that is unlawful, infringing, or that you do not have the rights to distribute.',
        'Use automated tools to scrape, extract, or redistribute content or data from the Service.',
      ],
    ],
  },
  {
    heading: '7. Termination',
    body: [
      'We may suspend or terminate your access to the Service if you violate these Terms, misuse the platform, or if required by law. You may stop using the Service and close your account at any time.',
    ],
  },
  {
    heading: '8. Disclaimers & Limitation of Liability',
    body: [
      'The Service is provided "as is" and "as available" without warranties of any kind, express or implied. We do not guarantee that content will always be available, uninterrupted, or error-free.',
      'To the fullest extent permitted by law, Kast will not be liable for any indirect, incidental, or consequential damages arising from your use of the Service.',
    ],
  },
  {
    heading: '9. Changes to These Terms',
    body: [
      'We may update these Terms from time to time. If we make material changes, we will notify you through the Service or by other reasonable means before the changes take effect. Continued use of Kast after changes take effect constitutes acceptance of the updated Terms.',
    ],
  },
  {
    heading: '10. Contact Us',
    body: [
      'If you have questions about these Terms, please reach out to us through the Help Centre.',
    ],
  },
];

export default function TermsPage() {
  usePageTitle('Terms of Use · Kast', 'Read the terms that govern your use of Kast.');

  return (
    <LegalPage
      title="Terms of Use"
      lead="Please read these terms carefully before using Kast. They explain the rules that govern your account, subscription, and use of our content."
      updated="October 20, 2026"
      sections={SECTIONS}
    />
  );
}
